import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function DataFormatDocs() {
  useEffect(() => {
    document.title = "Data Format Documentation | Habitualy";
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container max-w-4xl">
          <div className="space-y-8">
            {/* Header */}
            <div className="space-y-4">
              <h1 className="text-4xl font-bold">Data Format Documentation</h1>
              <p className="text-lg text-muted-foreground">
                Learn about the CSV format used for importing and exporting your habit data.
              </p>
            </div>

            {/* CSV Format Overview */}
            <Card>
              <CardHeader>
                <CardTitle>CSV Format Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  Habitualy uses a simple CSV (Comma-Separated Values) format for data import and export. 
                  This format is compatible with spreadsheet applications like Excel, Google Sheets, and Numbers.
                  The format supports both habit tracking data and mood tracking data.
                </p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold">File Requirements:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>File extension must be <code className="bg-muted px-1 rounded">.csv</code></li>
                    <li>UTF-8 encoding recommended</li>
                    <li>Comma-separated values with quoted fields</li>
                    <li>First row must contain headers</li>
                    <li>Supports both legacy format (habits only) and new format (habits + mood data)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Headers */}
            <Card>
              <CardHeader>
                <CardTitle>CSV Format Headers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Habitualy supports two CSV formats:</p>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">New Format (Recommended) - Includes Mood Data</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      This format supports both habits and mood tracking data:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Badge variant="outline">Type</Badge>
                        <p className="text-sm text-muted-foreground">
                          Either "Habit" or "Mood"
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="outline">Name</Badge>
                        <p className="text-sm text-muted-foreground">
                          Habit name or "Mood Tracker"
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="outline">Created At</Badge>
                        <p className="text-sm text-muted-foreground">
                          When the habit was created
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="outline">Status</Badge>
                        <p className="text-sm text-muted-foreground">
                          "Active" or "Paused"
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="outline">Completed Dates</Badge>
                        <p className="text-sm text-muted-foreground">
                          Habit completion dates (YYYY-MM-DD)
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="outline">Mood Data</Badge>
                        <p className="text-sm text-muted-foreground">
                          Mood entries (YYYY-MM-DD:Mood format)
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Legacy Format - Habits Only</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      This format is still supported for backwards compatibility:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Badge variant="secondary">Habit Name</Badge>
                        <p className="text-sm text-muted-foreground">
                          The name of your habit
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="secondary">Created At</Badge>
                        <p className="text-sm text-muted-foreground">
                          When the habit was created
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="secondary">Status</Badge>
                        <p className="text-sm text-muted-foreground">
                          "Active" or "Paused"
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Badge variant="secondary">Completed Dates</Badge>
                        <p className="text-sm text-muted-foreground">
                          Completion dates (YYYY-MM-DD)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Example */}
            <Card>
              <CardHeader>
                <CardTitle>Example CSV Formats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">New Format Example (with Mood Data)</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      This example shows both habits and mood tracking data:
                    </p>
                    
                    <div className="bg-muted p-4 rounded-lg overflow-x-auto">
                      <pre className="text-sm">
{`"Type","Name","Created At","Status","Completed Dates","Mood Data"
"Habit","Exercise","1/15/2024","Active","2024-01-15, 2024-01-16, 2024-01-18",""
"Habit","Read","1/10/2024","Active","2024-01-10, 2024-01-11, 2024-01-12",""
"Habit","Meditate","1/20/2024","Paused","2024-01-20, 2024-01-21",""
"Mood","Mood Tracker","","Active","","2024-01-15:Easy, 2024-01-16:Meh, 2024-01-17:Struggled"`}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Legacy Format Example (Habits Only)</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      This format is still supported for backwards compatibility:
                    </p>
                    
                    <div className="bg-muted p-4 rounded-lg overflow-x-auto">
                      <pre className="text-sm">
{`"Habit Name","Created At","Status","Completed Dates"
"Exercise","1/15/2024","Active","2024-01-15, 2024-01-16, 2024-01-18"
"Read","1/10/2024","Active","2024-01-10, 2024-01-11, 2024-01-12"
"Meditate","1/20/2024","Paused","2024-01-20, 2024-01-21"`}
                      </pre>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Import Behavior */}
            <Card>
              <CardHeader>
                <CardTitle>Import Behavior</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>When you import a CSV file, Habitualy follows these rules:</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-3">Habit Data Import</h4>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-semibold text-green-600">✓ New Habits</h5>
                        <p className="text-sm text-muted-foreground">
                          If a habit name doesn't exist, a new habit will be created with all the completion dates.
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-blue-600">✓ Existing Habits</h5>
                        <p className="text-sm text-muted-foreground">
                          If a habit name already exists, the completion dates will be merged. 
                          No existing dates will be lost, and no duplicates will be created.
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-purple-600">✓ Date Preservation</h5>
                        <p className="text-sm text-muted-foreground">
                          All existing completion dates are preserved. Only new dates from the import are added.
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-orange-600">✓ Status Updates</h5>
                        <p className="text-sm text-muted-foreground">
                          The habit's active/paused status will be updated to match the imported data.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Mood Data Import</h4>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-semibold text-pink-600">✓ Mood Entries</h5>
                        <p className="text-sm text-muted-foreground">
                          Mood entries are imported and merged with existing mood data. Each entry contains a date and mood value (Easy, Meh, or Struggled).
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-cyan-600">✓ Mood Format</h5>
                        <p className="text-sm text-muted-foreground">
                          Mood data should be in the format: <code className="bg-muted px-1 rounded">YYYY-MM-DD:Mood</code> where Mood is one of: Easy, Meh, Struggled.
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-indigo-600">✓ Mood Merging</h5>
                        <p className="text-sm text-muted-foreground">
                          If a mood entry already exists for a specific date, it will be updated with the imported value.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Format Detection</h4>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-semibold text-emerald-600">✓ Automatic Detection</h5>
                        <p className="text-sm text-muted-foreground">
                          Habitualy automatically detects whether you're using the new format (with Type column) or legacy format (habits only).
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-teal-600">✓ Backwards Compatibility</h5>
                        <p className="text-sm text-muted-foreground">
                          Legacy CSV files (without mood data) will continue to work exactly as before.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Format */}
            <Card>
              <CardHeader>
                <CardTitle>Date Format Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>Completion dates must follow specific formatting rules:</p>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold">Required Format</h4>
                    <p className="text-sm">
                      <code className="bg-muted px-2 py-1 rounded">YYYY-MM-DD</code> (ISO 8601 format)
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold">Valid Examples</h4>
                    <ul className="text-sm space-y-1">
                      <li><code className="bg-muted px-1 rounded">2024-01-15</code> ✓</li>
                      <li><code className="bg-muted px-1 rounded">2024-12-31</code> ✓</li>
                      <li><code className="bg-muted px-1 rounded">2023-06-01</code> ✓</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold">Invalid Examples</h4>
                    <ul className="text-sm space-y-1">
                      <li><code className="bg-muted px-1 rounded">01/15/2024</code> ✗</li>
                      <li><code className="bg-muted px-1 rounded">15-01-2024</code> ✗</li>
                      <li><code className="bg-muted px-1 rounded">Jan 15, 2024</code> ✗</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle>Tips & Best Practices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold">📋 Before Importing</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Always export your current data as a backup before importing</li>
                      <li>Verify your CSV format matches the required structure</li>
                      <li>Check that all dates are in YYYY-MM-DD format</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold">💾 Data Management</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Export your data regularly as a backup</li>
                      <li>Use descriptive habit names that won't conflict</li>
                      <li>Keep your CSV files organized with dates in the filename</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold">🔧 Troubleshooting</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>If import fails, check that all required headers are present</li>
                      <li>Ensure there are no empty habit names</li>
                      <li>Verify all dates follow the YYYY-MM-DD format</li>
                      <li>Make sure the file is saved as .csv format</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Premium Note */}
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span>💎</span>
                  Premium Feature
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  Data import is available exclusively to Premium users. Export functionality 
                  is available to all users. <a href="/pricing" className="text-primary hover:underline">
                  Upgrade to Premium</a> to unlock data import and other advanced features.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 